import React, { ReactElement } from "react";

import { Stack, Typography } from "@mui/material";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";

import Quota from "../../../../components/Quota";
import { ReduxTextField } from "../../../../components/Redux/TextField";
import SettingSection from "../../../../components/SettingSection";
import TextField from "../../../../components/styled/TextField";
import useFeatureFlag from "../../../../hooks/use-feature-flag";
import {
  getClusterFormProperty,
  setClusterFormProperty,
  setSettingsFormError,
} from "../../../../slices/settings";
import { useSelector } from "../../../../store";
import {
  useGetClusterQuery,
  useListCustomDomainsQuery,
} from "../../../../store/pomeriumApi";
import validIp from "../../../../validators/ip";
import { SectionProps } from "../../../routes/components/edit_sections/common";
import CustomDomains from "../CustomDomains";

export const Domains = ({
  readonly,
  tabValue,
  refresh,
  isHosted,
}: SectionProps & { isHosted: boolean }): ReactElement => {
  const fqdn = useSelector(getClusterFormProperty("fqdn", "")) as string;
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const hostedClustersUnconstrained = useFeatureFlag(
    "hosted-clusters-unconstrained",
    false,
  );
  const showCustomDomains = !isHosted || hostedClustersUnconstrained;

  const { data: cluster } = useGetClusterQuery(
    { organizationId, clusterId },
    { skip: !organizationId || !clusterId },
  );
  const { data: customDomains } = useListCustomDomainsQuery(
    { organizationId, clusterId },
    {
      skip:
        !organizationId ||
        !clusterId ||
        (isHosted && !hostedClustersUnconstrained),
    },
  );

  return (
    <Container component={Paper} maxWidth={false} disableGutters={true}>
      <Stack key={tabValue + refresh} spacing={1} sx={{ m: 3 }}>
        <Typography id={tabValue} variant="h2" sx={{ pt: 2 }}>
          Domains
        </Typography>
        <Divider />
        <SettingSection label="Starter Domain" docsKey="cluster-starter-domain">
          <TextField disabled error={!fqdn} fullWidth value={"*." + fqdn} />
        </SettingSection>
        <Divider />
        {!isHosted ? (
          <>
            <SettingSection
              label="Override IP Address"
              docsKey="override-ip-address"
            >
              <ReduxTextField
                disabled={readonly}
                setter={setClusterFormProperty}
                getter={getClusterFormProperty}
                errorHandler={setSettingsFormError}
                path="manualOverrideIpAddress"
                validator={validIp}
                fullWidth
              />
            </SettingSection>
            <Divider />

            <SettingSection
              label="Detected IP Address"
              docsKey="detected-ip-address"
            >
              <TextField
                fullWidth
                value={cluster?.autoDetectIpAddress || ""}
                disabled
                placeholder="ip address not detected"
              />
            </SettingSection>
            <Divider />
          </>
        ) : null}

        {showCustomDomains && (
          <SettingSection
            docsKey="custom-domains"
            label="Custom Domain"
            extraElement={<Quota quota="customDomains" label="domains" />}
          >
            <CustomDomains
              readonly={readonly}
              organizationId={organizationId}
              clusterId={clusterId}
              customDomains={customDomains}
            />
          </SettingSection>
        )}
      </Stack>
    </Container>
  );
};
